import { Bell, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  isWebNotificationSupported,
  readWebNotificationPermission,
  type WebNotificationPermission,
} from '@/hooks/useWebNotifications';

/** localStorage key recording that the user dismissed the soft prompt. */
const DISMISSED_KEY = 'webnotif:promptDismissed';

/** Delay before the soft prompt appears, so it does not collide with page load. */
const APPEAR_DELAY_MS = 4_000;

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Storage disabled — prompt will simply reappear next session.
  }
}

/**
 * Soft, in-app pre-prompt that asks the user to enable browser notifications.
 *
 * We never call {@link Notification.requestPermission} on page load: a browser
 * permanently blocks the origin if the native prompt is dismissed. Instead this
 * card explains the value first and only fires the native prompt when the user
 * actively clicks "Enable". It appears only when:
 *   - the Notification API is supported,
 *   - permission is still `default` (not yet granted or denied), and
 *   - the user has not previously dismissed it.
 *
 * Once granted or denied, the card stays hidden. Denied users are respected —
 * we do not nag them; re-enabling is done through their browser site settings.
 */
export function NotificationActivationPrompt(): React.JSX.Element | null {
  const [permission, setPermission] = useState<WebNotificationPermission>(
    readWebNotificationPermission,
  );
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!isWebNotificationSupported()) return;
    if (permission !== 'default' || wasDismissed()) return;

    const timer = window.setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [permission]);

  if (!visible || permission !== 'default') return null;

  const handleEnable = async (): Promise<void> => {
    setRequesting(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        // Register this browser for web push so backgrounded tabs still get
        // time-critical alerts. No-op when push is not configured.
        const { registerWebPush } = await import('@/lib/push');
        await registerWebPush();
      }
    } catch {
      // Older browsers reject the promise form — collapse to current state.
      setPermission(readWebNotificationPermission());
    } finally {
      setRequesting(false);
      setVisible(false);
    }
  };

  const handleDismiss = (): void => {
    markDismissed();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Enable browser notifications"
      className="fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-xl border p-4 shadow-lg"
      style={{
        background: 'var(--bg-surface-raised)',
        borderColor: 'var(--border-default)',
      }}
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 rounded-md p-1 text-[var(--text-secondary)]
          hover:text-[var(--text-primary)]"
      >
        <X className="size-4" />
      </button>

      <div className="flex items-start gap-3 pr-4">
        <span
          className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--bg-surface-highest)', color: 'var(--text-accent)' }}
        >
          <Bell className="size-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-primary)]">Turn on notifications</p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Get alerted when visitors arrive, deliveries are dropped off, and approvals need your
            attention — even when this tab is in the background.
          </p>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={handleDismiss} disabled={requesting}>
          Not now
        </Button>
        <Button size="sm" onClick={() => void handleEnable()} disabled={requesting}>
          {requesting ? 'Enabling…' : 'Enable'}
        </Button>
      </div>
    </div>
  );
}
